import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectRemoteMCPServers,
  selectRemoteMCPConnections,
  selectRemoteMCPLoading,
  selectConnectingServerId,
} from '@/redux/slices/remoteMCPSlice'
import {
  fetchRemoteMCPServersThunk,
  fetchRemoteMCPConnectionsThunk,
  startRemoteMCPOAuthThunk,
  disconnectRemoteMCPThunk,
} from '@/redux/asyncThunks/remoteMCP'
import { RemoteMCPServer } from '@/api/remoteMCP'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { FlaskConical, Link2, Link2Off, Loader2 } from 'lucide-react'

export function SciteConnectionPanel() {
  const dispatch = useAppDispatch()
  const servers = useAppSelector(selectRemoteMCPServers)
  const connections = useAppSelector(selectRemoteMCPConnections)
  const loading = useAppSelector(selectRemoteMCPLoading)
  const connectingServerId = useAppSelector(selectConnectingServerId)
  const connectingToastId = useRef<string | number | null>(null)

  useEffect(() => {
    dispatch(fetchRemoteMCPServersThunk())
    dispatch(fetchRemoteMCPConnectionsThunk())
  }, [dispatch])

  useEffect(() => {
    const handleFocus = () => {
      const wasConnecting = connectingToastId.current !== null
      if (wasConnecting) {
        toast.dismiss(connectingToastId.current!)
        connectingToastId.current = null
      }
      dispatch(fetchRemoteMCPConnectionsThunk()).then((result) => {
        if (
          wasConnecting &&
          fetchRemoteMCPConnectionsThunk.fulfilled.match(result)
        ) {
          const hasActive = result.payload.some((c) => c.is_active)
          if (hasActive) {
            toast.success('MCP service connected successfully!')
          }
        }
      })
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [dispatch])

  const getConnectionForServer = (server: RemoteMCPServer) =>
    connections.find((c) => c.mcp_server.id === server.id && c.is_active) ?? null

  const handleConnect = async (server: RemoteMCPServer) => {
    try {
      await dispatch(startRemoteMCPOAuthThunk(server.id)).unwrap()
      connectingToastId.current = toast.loading(
        `Authorizing ${server.name}… complete sign-in in the new tab`
      )
    } catch {
      if (connectingToastId.current !== null) {
        toast.dismiss(connectingToastId.current)
        connectingToastId.current = null
      }
      toast.error('Could not start connection. Please try again.')
    }
  }

  const handleDisconnect = async (connId: number, serverName: string) => {
    try {
      await dispatch(disconnectRemoteMCPThunk(connId)).unwrap()
      toast.success(`Disconnected from ${serverName}`)
    } catch {
      toast.error('Failed to disconnect. Please try again.')
    }
  }

  if (loading && servers.length === 0) {
    return (
      <div className='flex items-center gap-2 py-4 text-muted-foreground text-sm'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading research tools…
      </div>
    )
  }

  if (!loading && servers.length === 0) {
    return null
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <FlaskConical className='h-4 w-4 text-muted-foreground' />
        <h3 className='text-sm font-semibold'>External MCP Services</h3>
      </div>

      <p className='text-xs text-muted-foreground'>
        Connect external MCP services to give LLMs access to additional
        tools and data sources during conversations.
      </p>

      <div className='space-y-3'>
        {servers.map((server) => {
          const conn = getConnectionForServer(server)
          const isConnecting = connectingServerId === server.id

          return (
            <div
              key={server.id}
              className='flex items-start justify-between rounded-lg border p-3 gap-3'
            >
              <div className='min-w-0 flex-1 space-y-1'>
                <div className='flex items-center gap-2 flex-wrap'>
                  <span className='text-sm font-medium'>{server.name}</span>
                  {conn ? (
                    <Badge
                      variant='outline'
                      className='text-xs text-green-600 border-green-300 bg-green-50'
                    >
                      Connected
                    </Badge>
                  ) : (
                    <Badge variant='outline' className='text-xs text-muted-foreground'>
                      Not Connected
                    </Badge>
                  )}
                </div>
                {server.description && (
                  <p className='text-xs text-muted-foreground'>
                    {server.description}
                  </p>
                )}
                {conn && conn.token_expires_at && (
                  <p className='text-xs text-muted-foreground'>
                    Token expires:{' '}
                    {new Date(conn.token_expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              <div className='flex-shrink-0'>
                {conn ? (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='text-xs text-destructive hover:text-destructive'
                    onClick={() => handleDisconnect(conn.id, server.name)}
                  >
                    <Link2Off className='h-3.5 w-3.5 mr-1' />
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    variant='outline'
                    size='sm'
                    className='text-xs'
                    disabled={isConnecting}
                    onClick={() => handleConnect(server)}
                  >
                    {isConnecting ? (
                      <Loader2 className='h-3.5 w-3.5 mr-1 animate-spin' />
                    ) : (
                      <Link2 className='h-3.5 w-3.5 mr-1' />
                    )}
                    {isConnecting ? 'Opening…' : 'Connect'}
                  </Button>
                )}
              </div>
            </div>
          )
        })}
      </div>

    </div>
  )
}
